// A name resolved from another read (a work centre, a product), with the three
// honest states — module 06's `RefLabel` (areas/office/inventory/components/
// inventory-ui.tsx), which this file is a deliberate sibling of.
//
// WHY IT EXISTS HERE (builder report, 2026-08-12). Three module-04 surfaces
// fell back to the RAW UUID when a reference did not resolve:
//
//   * S5's scoreboard printed `work_center_id` as the station name whenever the
//     work-centre list had not answered (or the station was outside the page
//     it fetched) — so a supervisor's row was headed
//     `0191f3c1-8a4e-7c33-9b2d-…` where a machine name belongs;
//   * S2's Materials history and Genealogy tab did the same with
//     `component_product_id` / `product_id`.
//
// A uuid in a name column reads as corrupt data, and — worse — it reads
// IDENTICALLY whether the index is still loading or the id genuinely does not
// resolve. Those are different facts: one resolves itself in 200 ms, the other
// never will. So: the resolved text; a resolving marker while the index is in
// flight; otherwise the SHORT id in a `<code>` chip, which reads as an
// identifier rather than as a broken name.
//
// The chip is deliberately 8 characters: enough to quote in a support thread,
// short enough that nobody mistakes it for a name.

import { useTranslation } from "react-i18next";
import { shortId } from "../../../../lib/short-id";

export function RefLabel({
  value,
  id,
  pending = false,
}: {
  /** The resolved name, or undefined when the index has no row for `id`. */
  value: string | undefined;
  id: string | null | undefined;
  /** True while the index that would resolve `id` is still fetching. */
  pending?: boolean;
}) {
  const { t } = useTranslation();
  if (value !== undefined && value.length > 0) return <>{value}</>;
  if (id === null || id === undefined || id.length === 0) {
    return <span className="production-hint">{t("production.common.notAvailable")}</span>;
  }
  if (pending) {
    return (
      <span className="ref-pending" aria-label={t("production.common.loading")}>
        …
      </span>
    );
  }
  return <code className="ref-id">{shortId(id)}</code>;
}
